import React, { useEffect, useMemo, useState, useRef } from 'react';
import { FaEye, FaSearch, FaList, FaCheckCircle } from 'react-icons/fa';
import { MdUploadFile } from 'react-icons/md';
import CustomFormGroup from '../components/CustomFormGroup';
import MessageModal from '../components/MessageModal';
import Pagination from '../components/Pagination';
import Table from '../components/Table';
import CommonButton from '../components/CommonButton';
import { useLoading } from '../components/LoadingContext';
import {
  fetchDonationsByEvent,
  fetchEventList,
  approveDonation,
  uploadDonationProof,
  uploadProofFile,
  fetchProofUrl,
} from '../services/api/donationService';
import '../styles/Approval.css';

const DonationPage = () => {
  const [donations, setDonations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [proofFiles, setProofFiles] = useState({});
  const fileInputRef = useRef(null);
  const { showLoading, hideLoading } = useLoading();
  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user?.id;
  const params = new URLSearchParams(window.location.search);
  const defaultEventId = params.get('event_id') || '';

  const limitOptions = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: 'All', value: 'All' },
  ];

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    loadDonations();
  }, [selectedEventId, searchTerm]);

  const loadEvents = async () => {
    try {
      const data = await fetchEventList();
      const eventsData = data || [];
      setEvents(eventsData);
      const useDefaultEvent = defaultEventId && eventsData.some((event) => event.id === defaultEventId);
      setSelectedEventId(useDefaultEvent ? defaultEventId : eventsData[0]?.id || '');
    } catch (error) {
      console.error('loadEvents error:', error);
    }
  };

  const loadDonations = async () => {
    try {
      showLoading();
      const data = await fetchDonationsByEvent(selectedEventId || null);
      setDonations(data || []);
      setTotalCount(data?.length || 0);
    } catch (error) {
      console.error('loadDonations error:', error);
    } finally {
      hideLoading();
    }
  };

  const handleApprove = async (id) => {
    try {
      showLoading();
      await approveDonation(id, userId);
      setModalMessage('Donation verified successfully.');
      setIsSuccess(true);
      setIsModalOpen(true);
      await loadDonations();
    } catch (error) {
      console.error('handleApprove error:', error);
      setModalMessage('Failed to verify donation.');
      setIsSuccess(false);
      setIsModalOpen(true);
    } finally {
      hideLoading();
    }
  };

  const handleUploadClick = (donationId) => {
    setUploadingId(donationId);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !uploadingId) return;

    const filePath = `donation_proof/${uploadingId}_${Date.now()}_${file.name}`;

    try {
      showLoading();
      await uploadProofFile(filePath, file);
      await uploadDonationProof(uploadingId, filePath, userId);
      setModalMessage('Proof uploaded successfully.');
      setIsSuccess(true);
      setIsModalOpen(true);
      await loadDonations();
    } catch (error) {
      console.error('handleFileChange error:', error);
      setModalMessage('Failed to upload proof.');
      setIsSuccess(false);
      setIsModalOpen(true);
    } finally {
      hideLoading();
      setUploadingId(null);
      event.target.value = '';
    }
  };

  const handlePreview = async (filePath) => {
    if (!filePath) {
      setModalMessage("No proof available.");
      setIsSuccess(false);
      setIsModalOpen(true);
      return;
    }

    try {
      showLoading();

      const session = JSON.parse(localStorage.getItem("sb-session"));
      const token = session?.access_token;
      const url = await fetchProofUrl(filePath);

      console.log("Download URL:", url);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Status:", response.status);

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const blob = await response.blob();

      console.log("Blob:", blob);

      const blobUrl = URL.createObjectURL(blob);

      setPreviewUrl(blobUrl);
      setIsModalOpen(true);
    } catch (err) {
      console.error(err);

      setModalMessage("Unable to load proof image.");
      setIsSuccess(false);
      setIsModalOpen(true);
    } finally {
      hideLoading();
    }
  };

  const handleCloseModal = () => {
    if (previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(null);
    setIsModalOpen(false);
  };

  const handleLimitChange = (e) => {
    const value = e.target.value;
    setLimit(value === 'All' ? 'All' : parseInt(value));
    setCurrentPage(1);
  };

  const filteredDonations = useMemo(() => {
    if (!searchTerm) return donations;
    const keyword = searchTerm.toLowerCase();
    return donations.filter((item) => {
      const name = item.guest?.name?.toLowerCase() || '';
      const phone = item.guest?.phone_number || '';
      return name.includes(keyword) || phone.includes(keyword);
    });
  }, [donations, searchTerm]);

  const paginatedDonations = useMemo(() => {
    if (limit === 'All') return filteredDonations;
    const start = (currentPage - 1) * limit;
    return filteredDonations.slice(start, start + limit);
  }, [filteredDonations, currentPage, limit]);

  const columns = [
    { key: 'guest_name', label: 'Guest Name' },
    { key: 'guest_phone', label: 'Phone' },
    { key: 'amount', label: 'Amount' },
    { key: 'uploaded_at', label: 'Uploaded At' },
    { key: 'uploaded_by', label: 'Uploaded By' },
    { key: 'verified_at', label: 'Verified At' },
    { key: 'payment_status', label: 'Verified', format: (value) => (value ? 'Yes' : 'No') },
    { key: 'verified_by', label: 'Verified By' },
  ];

  const data = paginatedDonations.map((item) => ({
    id: item.id,
    guest_name: item.guest?.name || '-',
    guest_phone: item.guest?.phone_number || '-',
    amount: item.amount?.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' }) || '-',
    uploaded_at: item.uploaded_at ? new Date(item.uploaded_at).toLocaleString('id-ID') : '-',
    uploaded_by: item.uploaded_by || '-',
    verified_at: item.verified_at ? new Date(item.verified_at).toLocaleString('id-ID') : '-',
    payment_status: item.payment_status,
    verified_by: item.verified_by || '-',
    proof_file: item.proof_file,
  }));

  return (
    <div className="approval-page-container">
      <h2>Event Donation</h2>
      <div className="approval-controls">
        <CustomFormGroup
          type="select"
          options={[{ label: 'All Events', value: '' }, ...(events || []).map((event) => ({ label: event.name || 'Unnamed event', value: event.id }))]}
          value={selectedEventId}
          onChange={(e) => {
            setSelectedEventId(e.target.value);
            setCurrentPage(1);
          }}
          icon={<FaList />}
        />
        <CustomFormGroup
          type="select"
          options={limitOptions}
          value={limit}
          onChange={handleLimitChange}
          icon={<FaList />}
        />
        <CustomFormGroup
          type="text"
          placeholder="Search by guest or phone"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          icon={<FaSearch />}
        />
      </div>

      <Table
        columns={columns}
        data={data}
        actions={(row) => (
          <>
            <CommonButton
              className="table-button"
              onClick={() => handlePreview(row.proof_file)}
            >
              <FaEye />
            </CommonButton>
            {user?.role_id === 5 && !row.proof_file && (
              <CommonButton
                className="table-button"
                onClick={() => handleUploadClick(row.id)}
              >
                <MdUploadFile />
              </CommonButton>
            )}
            {[1, 2].includes(user?.role_id) && (
              <CommonButton
                className="table-button"
                onClick={() => handleApprove(row.id)}
              >
                <FaCheckCircle />
              </CommonButton>
            )}
          </>
        )}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={limit === 'All' ? 1 : Math.ceil(filteredDonations.length / limit)}
        onPageChange={(page) => setCurrentPage(page)}
      />

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />

      {isModalOpen && (
        <MessageModal
          onClose={handleCloseModal}
          title={previewUrl ? 'Proof Preview' : isSuccess ? 'Success' : 'Failed'}
          message_header={previewUrl ? '' : ''}
          icon={null}
          message_content={
            previewUrl ? (
              <img src={previewUrl} alt="Donation Proof" style={{ maxWidth: '100%', borderRadius: 8 }} />
            ) : (
              modalMessage
            )
          }
          button_name="Close"
          isSuccess={isSuccess}
        />
      )}
    </div>
  );
};

export default DonationPage;
